import React, { useEffect, useState } from 'react'

import _ from 'lodash'
import cx from 'classnames'
import { Form } from 'react-bootstrap'

import { Fonts } from '@paperflite-marketing/cleverstory-commons'
import { getColorsConfig } from '../../../../AssetViewer/helper/cssHelper'
import { FormWidgetContext } from '../..'
import { injectAktivFallback } from '../../../../widgets/widget.helper'

import styles from './checkbox.module.scss'

const FormCheckBox = React.memo(({ className, widgetProps, style, breakpoint }) => {
  const context = React.useContext(FormWidgetContext)

  const [value, setValue] = React.useState(false);
  const [errors, setErrors] = React.useState('');

  const id = _.get(widgetProps, 'uid')
  const properties = _.get(widgetProps, 'properties', {})

  const instructions = _.get(widgetProps, 'content.instructions') || '';
  const initialState = _.get(widgetProps, 'content.initialState') || false;
  const field = _.get(widgetProps, 'properties.desktop.field') || '';
  const fieldId = _.get(widgetProps, 'content.id') || "";
  const error = _.get(context.errors, id);

  useEffect(() => {
    setErrors('');
    context.updateField(id, field, fieldId, value)
  }, [value])

  const handleChange = (value) => {
    setValue(value);
  }

  useEffect(()=>{
    setValue(initialState);
  }, [initialState])

  useEffect(()=>{
    setErrors(error);
  }, [error])

  Fonts.useFont(_.get(widgetProps, 'fonts') || []);
  var checkBoxStyles = getCheckBoxStyles(properties, 'desktop', 'regular');
  var instuctionStyles = getInstuctionStyles(properties, breakpoint, 'regular');

  return (
    <div
      id={`checkbox-wrapper-${id}`}
      className={cx(className, styles.inputField)}
      style={style}
      data-widget-id={`checkbox-wrapper-${id}`}
    >
      <style>
        {`
          #checkbox-input-${id}:checked[type=checkbox] {
            background-color: ${_.get(checkBoxStyles, 'color')};
            box-shadow: 'none';
            border-color: ${_.get(checkBoxStyles, 'color')};
          }
        `}
      </style>
      <Form.Check type='checkbox' id={`checkbox-${id}}`} className={styles.checkBoxItem}>
        <Form.Check.Input
          style={checkBoxStyles}
          id={`checkbox-input-${id}`}
          type='checkbox'
          checked={value}
          value={value}
          onMouseDown={(e) => handleChange(!e.target.checked)
          }
        />
        <Form.Check.Label style={instuctionStyles} >{instructions}</Form.Check.Label>
        {errors && (
          <Form.Control.Feedback
            className={cx(styles.error, { [styles.active]: errors })}
            type='invalid'
          >
            {errors}
          </Form.Control.Feedback>
        )}
      </Form.Check>
      <span className={cx(styles.error, { [styles.active]: errors })}>{errors}</span>
    </div>
  )
})

/**
 * --------------------------------------------------
 * Field Styles
 * --------------------------------------------------
 */
function getCheckBoxStyles(properties, breakpoint, active) {
  var styles = {}

  if (!properties) {
    return styles
  }

  const defaultProperties = _.get(properties, `desktop`)
  const inputStyles = _.get(defaultProperties, 'styles.regular.input')
  //color
  const colorRgb = _.get(inputStyles, 'color', null)
  const color = getColorStyles(colorRgb)
  
  styles = {
    ...styles,
    ...color,
    borderColor: color
  }

  return styles
}

/**
 * --------------------------------------------------
 * Input Styles
 * --------------------------------------------------
 */
function getInstuctionStyles(properties, breakpoint, active) {
  var styles = {}

  if (!properties) {
    return styles
  }

  const defaultProperties = _.get(properties, `desktop`)
  const deviceProperties = _.get(properties, `${breakpoint}`)
  const inputPadding = _.get(deviceProperties, 'styles.layout.inputPadding') || 0


  styles['paddingLeft'] = `${inputPadding}px`

  const inputStyles = _.get(defaultProperties, 'styles.regular.input')
  const fontStyles = getFontStyle(inputStyles)

  styles = {
    ...styles,
    ...fontStyles,
    color: getPlaceholderColor(properties)
  }

  return styles
}

/**
 * --------------------------------------------------
 * Placeholder Styles
 * --------------------------------------------------
 */
export function getPlaceholderColor(properties) {
  var styles = {}

  if (!properties) {
    return styles
  }

  const defaultProperties = _.get(properties, `desktop`)
  const inputStyles = _.get(defaultProperties, 'styles.regular.input')

  const colorRgb = _.get(inputStyles, 'placeholderColor', null)
  if (!colorRgb) {
    return null
  }

  return getColorsConfig(colorRgb)
}

export function getColorStyles(value) {
  if (!value) {
    return null
  }

  const colorRgb = value

  var color = 'inherit'
  if (colorRgb) {
    color = getColorsConfig(colorRgb)
  }

  var styles = {
    color: color
  }

  return styles
}

export function getBorderStyles(border) {
  var styles = {}
  if (!border) {
    return styles
  }

  const topLeft = _.get(border, 'radius.topLeft', null)
  const topRight = _.get(border, 'radius.topRight', null)
  const bottomLeft = _.get(border, 'radius.bottomLeft', null)
  const bottomRight = _.get(border, 'radius.bottomRight', null)

  if (topLeft) {
    styles['borderTopLeftRadius'] = parseInt(topLeft) + 'px'
  }

  if (topRight) {
    styles['borderTopRightRadius'] = parseInt(topRight) + 'px'
  }

  if (bottomLeft) {
    styles['borderBottomLeftRadius'] = parseInt(bottomLeft) + 'px'
  }

  if (bottomRight) {
    styles['borderBottomRightRadius'] = parseInt(bottomRight) + 'px'
  }

  const borderStyle = _.get(border, 'style') || 'bottom'
  if (borderStyle !== 'none') {
    const borderWidthString = _.get(border, 'width') || 0
    var borderWidget = parseInt(borderWidthString) + 'px'

    const fieldName = borderStyle == 'all' ? 'borderWidth' : 'borderBottomWidth'
    styles[fieldName] = borderWidget

    const colorRgb = _.get(border, 'color')
    var color = 'transparent'
    if (colorRgb) {
      color = getColorsConfig(colorRgb)
    }

    styles['borderColor'] = color
  }

  return styles
}

export function getFontStyle(fontStyles) {
  var styles = {}

  if (!fontStyles) {
    return styles
  }

  //font-color
  const colorRgb = _.get(fontStyles, 'color', null)
  const color = getColorStyles(colorRgb)
  styles = {
    ...styles,
    ...color
  }

  //font-family
  const fontFamily = _.get(fontStyles, 'font.fontFamily', 'inherit')
  styles = {
    ...styles,
    fontFamily: injectAktivFallback(fontFamily)
  }

  //font-size
  const fontSizeString = _.get(fontStyles, `font.fontSize`) || 14
  if (fontSizeString) {
    var fontSize = parseInt(fontSizeString) + 'px'
    styles['fontSize'] = fontSize
  }

  return styles
}

export const FormCheckBoxField = FormCheckBox
